import { useReducer, useEffect, useMemo } from "react";
import { SUITS, RANKS, cardId, buildShoe, hiLoValue, TOTAL_CARDS_PER_DECK } from "@/lib/deck";

const STORAGE_KEY = "card-counter-state-v1";

function isValid(state) {
  if (!state || typeof state.deckCount !== "number") return false;
  if (!state.counts || typeof state.counts !== "object") return false;
  if (!Array.isArray(state.history)) return false;
  // Sanity: dealt count must equal missing cards from a full shoe.
  const total = state.deckCount * TOTAL_CARDS_PER_DECK;
  let remaining = 0;
  for (const suit of SUITS)
    for (const rank of RANKS) remaining += state.counts[cardId(suit.id, rank)] ?? 0;
  return remaining === total - state.history.length;
}

function loadState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return isValid(parsed) ? parsed : null;
  } catch (err) {
    if (process.env.NODE_ENV === "development") {
      console.warn("Card Counter: failed to read saved state", err);
    }
    return null;
  }
}

function init() {
  const saved = loadState();
  if (saved) return saved;
  return { deckCount: 1, counts: buildShoe(1), history: [] };
}

function reducer(state, action) {
  switch (action.type) {
    case "DEAL": {
      const id = action.id;
      if ((state.counts[id] ?? 0) <= 0) return state;
      return {
        ...state,
        counts: { ...state.counts, [id]: state.counts[id] - 1 },
        history: [...state.history, id],
      };
    }
    case "UNDO": {
      if (state.history.length === 0) return state;
      const last = state.history[state.history.length - 1];
      return {
        ...state,
        counts: { ...state.counts, [last]: (state.counts[last] ?? 0) + 1 },
        history: state.history.slice(0, -1),
      };
    }
    case "RESET":
      return { ...state, counts: buildShoe(state.deckCount), history: [] };
    case "SET_DECKS":
      return { deckCount: action.n, counts: buildShoe(action.n), history: [] };
    default:
      return state;
  }
}

export function useShoe() {
  const [state, dispatch] = useReducer(reducer, undefined, init);
  const { deckCount, counts, history } = state;

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (err) {
      if (process.env.NODE_ENV === "development") {
        console.warn("Card Counter: failed to save state", err);
      }
    }
  }, [state]);

  const changeDeckCount = (n) => dispatch({ type: "SET_DECKS", n });
  const dealCard = (id) => dispatch({ type: "DEAL", id });
  const undo = () => dispatch({ type: "UNDO" });
  const reset = () => dispatch({ type: "RESET" });

  const stats = useMemo(() => {
    const totalCards = deckCount * TOTAL_CARDS_PER_DECK;
    const totalDealt = history.length;
    const totalRemaining = totalCards - totalDealt;

    const bySuit = {};
    for (const suit of SUITS) bySuit[suit.id] = 0;
    const byRank = {};
    for (const rank of RANKS) byRank[rank] = 0;

    let runningCount = 0;
    for (const suit of SUITS) {
      for (const rank of RANKS) {
        const rem = counts[cardId(suit.id, rank)] ?? 0;
        bySuit[suit.id] += rem;
        byRank[rank] += rem;
        const dealtOfThis = deckCount - rem;
        runningCount += dealtOfThis * hiLoValue(rank);
      }
    }

    const remainingDecks = totalRemaining / TOTAL_CARDS_PER_DECK;
    const trueCount = remainingDecks > 0 ? runningCount / remainingDecks : 0;

    return { totalCards, totalDealt, totalRemaining, bySuit, byRank, runningCount, remainingDecks, trueCount };
  }, [counts, history, deckCount]);

  return { deckCount, counts, history, changeDeckCount, dealCard, undo, reset, stats };
}
